import { Country } from 'APIs/fragments';
import { MerchantBusinessType } from 'APIs/merchants/merchants.types';
import { countries, merchantBusinessTypes } from 'Constants/Constants';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';

const payPlanVariants = [
  'payplan_fortnight_25_25_25_25',
  'payplan_4_weekly_payments',
  'payplan_8_weekly_payments',
  'payplan_26_fortnightly_payments',
  'payplan_52_weekly_payments',
  'payplan_2_monthly_payments',
  'payplan_3_monthly_payments',
  'payplan_4_monthly_payments',
  'payplan_5_monthly_payments',
  'payplan_6_monthly_payments',
  'payplan_7_monthly_payments',
  'payplan_8_monthly_payments',
  'payplan_9_monthly_payments',
  'payplan_10_monthly_payments',
  'payplan_11_monthly_payments',
  'payplan_12_monthly_payments',
  'payplan_fortnight_0_100',
  'payplan_4_week_0_100',
  'payplan_7_week_0_100',
  'payplan_30_days_0_100',
];

const customerTypes = ['consumercustomertype', 'organisationcustomertype', 'marketplacecustomerproxytype'];

const cardFundings = ['credit', 'debit', 'chargecard', 'prepaid', 'deferreddebit', 'unknown'];

const cardBrands = [
  '',
  'American Express',
  'MasterCard',
  'Visa',
  'Diners Club',
  'Discover',
  'UnionPay',
  'Maestro',
  'Elo',
  'Mir',
  'Hiper',
  'Hipercard',
  'Jcb',
  'Electron',
  'Uatp',
];

const feeStructExCardSchema = Yup.object({
  cardFunding: Yup.mixed<typeof cardFundings[number]>().oneOf(cardFundings).required(),
  cardBrand: Yup.mixed<typeof cardBrands[number]>().oneOf(cardBrands).notRequired(), // optional; if present can even be ''
  perc: Yup.number().min(0).required('Fee percentage is required'),
  fixed: Yup.number().min(0).required('Fee amount is required'),
}).noUnknown(true, 'Unknown keys in card');

const feeStructExFeeAmountSchema = Yup.object({
  perc: Yup.number().min(0).required('Fee percentage is required'),
  fixed: Yup.number().min(0).required('Fee amount is required'),
  cards: Yup.array().of(feeStructExCardSchema).notRequired().nullable(),
}).noUnknown(true, 'Unknown keys in feeAmount');

const feeStructExFeeTableSchema = Yup.object({
  domestic: feeStructExFeeAmountSchema.required('Domestic fee is required'),
  international: feeStructExFeeAmountSchema.required('International fee is required'),
}).noUnknown(true, 'Unknown keys in feeTable');

const feeStructExPayPlanFeeEntrySchema = Yup.object({
  payPlanVariant: Yup.mixed<typeof payPlanVariants[number]>()
    .oneOf(payPlanVariants)
    .required('Pay plan variant name is required'),
  customerType: Yup.mixed<typeof customerTypes[number]>().oneOf(customerTypes).required('Customer type is required'),
  feeTable: feeStructExFeeTableSchema.required('Pay plan variant fee table is required'),
}).noUnknown(true, 'Unknown keys in payPlanFeeEntry');

export const feeStructExSchema = Yup.object({
  payPlanFees: Yup.object({
    payPlanFeeEntries: Yup.array().of(feeStructExPayPlanFeeEntrySchema).required('Pay plan fee entries are required'),
  })
    .required('Pay plan fees are required')
    .noUnknown(true, 'Unknown keys in payPlanFees'),
  payCardFees: feeStructExFeeTableSchema.required('Pay card fees are required'),
  directDebitFees: feeStructExFeeAmountSchema.notRequired().nullable(),
})
  .noUnknown(true, 'Unknown keys in feeStructEx')
  .strict(false);

export const validationSchema = Yup.object().shape({
  businessName: Yup.string().trim().required('Business name is required'),
  businessType: Yup.string().oneOf(
    merchantBusinessTypes.map((b) => b.value as MerchantBusinessType),
    'Business type is required',
  ),
  industry: Yup.string().trim().required('Industry is required'),
  taxId: Yup.string().trim().required('Tax ID is required'),
  businessDisplayName: Yup.string().trim().required('Business display name is required'),
  businessWebsite: Yup.string().trim().url('Must be a valid URL').required('Business website is required'),
  supportEmail: Yup.string().trim().email('Must be a valid email').required('Support email is required'),
  iconUri: Yup.string().trim().url('Must be a valid URL').required('Icon URI is required'),
  logoUri: Yup.string().trim().url('Must be a valid URL').required('Logo URI is required'),
  logoAltText: Yup.string().trim().required('Logo ALT text is required'),
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  title: Yup.string().trim().required('Title name is required'),
  contactEmail: Yup.string().trim().email('Must be a valid email').required('Contact email is required'),
  phoneNo: Yup.string()
    .trim()
    .nullable()
    .test({
      name: 'phone-no-validation',
      test: function () {
        const { phoneNo } = this.parent;
        if (!phoneNo)
          return this.createError({
            message: `Phone number is required`,
            path: `phoneNo`,
          });
        if (!isPossiblePhoneNumber(phoneNo))
          return this.createError({
            message: `Phone number is invalid`,
            path: `phoneNo`,
          });
        return true;
      },
    }),
  address: Yup.object().shape({
    unitNumber: Yup.string().trim().nullable(),
    streetNumber: Yup.string().trim().required('Street number is required'),
    streetName: Yup.string().trim().required('Street name is required'),
    streetType: Yup.string().trim().required('Street type is required'),
    suburb: Yup.string().trim().nullable(),
    city: Yup.string().when('country', {
      is: (country: Country) => !['AU', 'US'].includes(country),
      then: Yup.string().trim().required('City is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    postalCode: Yup.string().trim().required('Postal code is required'),
    state: Yup.string().when('country', {
      is: (country: Country) => ['AU', 'US'].includes(country),
      then: Yup.string().trim().required('State is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    country: Yup.string().oneOf(countries.map((c) => c.value)),
  }),
  maxPayPlanAmountUnconfirmedCustomers: Yup.number()
    .typeError('Must be a number')
    .required('Maximum amount is required')
    .integer('Amount must be an integer'),

  maxPayPlanAmountConfirmedCustomers: Yup.number()
    .typeError('Must be a number')
    .required('Maximum amount is required')
    .integer('Amount must be an integer'),
  minPlanLimit: Yup.number()
    .typeError('Must be a number')
    .required('Maximum amount is required')
    .integer('Amount must be an integer'),
  paymentProfileType: Yup.string().trim().required('Payment profile is required'),
  feeStructEx: Yup.string()
    .required('Fee structure is required')
    .test('valid-fee-struct-ex-json', 'Invalid fee structure JSON', function (value) {
      const { createError, path } = this;

      if (value == null || value.trim() === '') {
        return createError({ path, message: 'Fee structure is required' });
      }

      let parsed;
      try {
        parsed = JSON.parse(value);
      } catch {
        return createError({ path, message: 'Fee structure must be valid JSON' });
      }

      try {
        feeStructExSchema.validateSync(parsed, { abortEarly: false });
        return true;
      } catch (err) {
        if (err instanceof Yup.ValidationError && err.errors && err.errors.length > 0) {
          return createError({ path, message: err.errors[0] });
        }
        return createError({ path, message: 'Invalid fee structure' });
      }
    }),
});
